import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { IDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { DataTypes } from "../../rule-engine-core/Common/Data/DataTypes";

import { DisplayModeNumberValueEditor, EditModeNumberValueEditor } from "./Number";
import { DisplayModeBooleanValueEditor, EditModeBooleanValueEditor } from "./Boolean";
import { DisplayModeStringValueEditor, EditModeStringValueEditor } from "./String";
import { DisplayModeDateValueEditor, EditModeDateValueEditor } from "./Date";

import { IDisplayModeValueEditor, IEditModeValueEditor, IValueEditorMode } from "./Common";
import { DisplayEditModeRender, EditLayerWrapper, EditModeValueEditorRender, ValueEditorWrapper } from "./DesignUI";

import { DisplayModeObjectValueEditor, EditModeObjectValueEditor } from "./Object";

import { DisplayModeFileValueEditor, EditModeFileValueEditor } from "./File";
import { StyledButton } from "../BuildlingBlocks/StyledButton";
import { UILayer } from "../BuildlingBlocks/UILayer";
import { StyledLink } from "../BuildlingBlocks/StyledLink";
import { ClickAwayListener } from "@mui/material";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import type { AppGlueAny } from "../../rule-engine-core/Common/AppGlueAny";

interface IDataEditControl {
    forData: IDataDefinition;
    value: AppGlueAny;
    onUpdate: (newValue: AppGlueAny) => void;
    mode: IValueEditorMode;
    navigate?: (direction: "Up" | "Down" | "Right" | "Left" | "Tab" | "Select") => void;
    editMode?: "inline" | "layer";
}

export function DataEditControl({ forData, value: propValue, onUpdate, mode, navigate, editMode }: IDataEditControl) {
    const [value, setValue] = useState<AppGlueAny>(propValue);

    const ref = React.useRef<HTMLElement>(null);

    useEffect(() => {
        setValue(propValue);
    }, [propValue, setValue]);

    const onValueUpdate = (newValue: AppGlueAny) => {
        setValue(newValue);
        onUpdate(newValue);
    };

    const onSetEdit = useCallback(
        (event: KeyboardEvent) => {
            // if (ref.current && ref.current !== document.activeElement) {
            //     ref.current.focus();
            // }
            if (navigate) {
                switch (event.key) {
                    case "Down": // IE/Edge specific value
                    case "ArrowDown":
                        if (ref.current instanceof HTMLSelectElement) {
                            ref.current.focus();
                        } else {
                            navigate("Down");
                        }
                        break;
                    case "Up": // IE/Edge specific value
                    case "ArrowUp":
                        navigate("Up");
                        break;
                    case "Left": // IE/Edge specific value
                    case "ArrowLeft":
                        // check if current focused element is input element and cursor is placed before first character.

                        if (ref.current) {
                            if (
                                ref.current instanceof HTMLInputElement &&
                                ["text"].includes(ref.current.getAttribute("type") || "") &&
                                (ref.current.selectionEnd || 0) === 0
                            ) {
                                navigate("Left");
                            } else if (!(ref.current instanceof HTMLInputElement)) {
                                navigate("Left");
                            } else if (
                                ref.current instanceof HTMLInputElement &&
                                !["text"].includes(ref.current.getAttribute("type") || "")
                            ) {
                                navigate("Left");
                            }
                        } else {
                            navigate("Left");
                        }

                        break;
                    case "Right": // IE/Edge specific value
                    case "ArrowRight":
                        // check if current focused element is input element and cursor is placed after last character.
                        if (ref.current) {
                            if (
                                ref.current instanceof HTMLInputElement &&
                                ["text"].includes(ref.current.getAttribute("type") || "") &&
                                (ref.current.selectionEnd || 0) >= (value || "").length - 1
                            ) {
                                navigate("Right");
                            } else if (!(ref.current instanceof HTMLInputElement)) {
                                navigate("Right");
                            } else if (
                                ref.current instanceof HTMLInputElement &&
                                !["text"].includes(ref.current.getAttribute("type") || "")
                            ) {
                                navigate("Right");
                            }
                        } else {
                            navigate("Right");
                        }
                        break;
                    case "Tab":
                        navigate("Tab");
                        break;
                    default:
                        return; // Quit when this doesn't handle the key event.
                }
            }
        },
        [navigate, ref, value],
    );

    React.useEffect(() => {
        // listen the keyboard events only for edit mode and display mode edit.
        if (mode === "display_mode_edit" || mode === "edit_mode") {
            window.addEventListener("keydown", onSetEdit);
        }
        return () => {
            if (mode === "display_mode_edit" || mode === "edit_mode") {
                window.removeEventListener("keydown", onSetEdit);
            }
        };
    }, [mode, onSetEdit]);

    switch (forData.type) {
        case DataTypes.BOOLEAN:
            return (
                <ValueEditorRender
                    forData={forData}
                    value={value}
                    onUpdate={onValueUpdate}
                    mode={mode}
                    ref={ref}
                    displayRenderUI={DisplayModeBooleanValueEditor}
                    editRenderUI={EditModeBooleanValueEditor}
                    editMode={editMode || "inline"}
                />
            );
        case DataTypes.STRING:
            return (
                <ValueEditorRender
                    forData={forData}
                    value={value}
                    onUpdate={onValueUpdate}
                    mode={mode}
                    ref={ref}
                    displayRenderUI={DisplayModeStringValueEditor}
                    editRenderUI={EditModeStringValueEditor}
                    editMode={editMode || "inline"}
                />
            );
        case DataTypes.DATE:
            return (
                <ValueEditorRender
                    forData={forData}
                    value={value}
                    onUpdate={onValueUpdate}
                    mode={mode}
                    ref={ref}
                    displayRenderUI={DisplayModeDateValueEditor}
                    editRenderUI={EditModeDateValueEditor}
                    editMode={editMode || "inline"}
                />
            );
        case DataTypes.NUMBER:
            return (
                <ValueEditorRender
                    forData={forData}
                    value={value}
                    onUpdate={onValueUpdate}
                    mode={mode}
                    ref={ref}
                    displayRenderUI={DisplayModeNumberValueEditor}
                    editRenderUI={EditModeNumberValueEditor}
                    editMode={editMode || "inline"}
                />
            );
        case DataTypes.FILE:
            return (
                <ValueEditorRender
                    forData={forData}
                    value={value}
                    onUpdate={onValueUpdate}
                    mode={mode}
                    ref={ref}
                    displayRenderUI={DisplayModeFileValueEditor}
                    editRenderUI={EditModeFileValueEditor}
                    editMode={editMode || "inline"}
                />
            );
        case DataTypes.OBJECT:
            return (
                <ValueEditorRender
                    forData={forData}
                    value={value}
                    onUpdate={onValueUpdate}
                    mode={mode}
                    ref={ref}
                    displayRenderUI={DisplayModeObjectValueEditor}
                    editRenderUI={EditModeObjectValueEditor}
                    editMode={editMode || "inline"}
                />
            );
        default:
            return null;
    }
}

interface IDataEditControlRenderer extends IDataEditControl {
    editRenderUI: React.ForwardRefExoticComponent<
        React.PropsWithoutRef<IEditModeValueEditor> & React.RefAttributes<HTMLElement>
    >;
    displayRenderUI: (props: IDisplayModeValueEditor) => JSX.Element | null;
}

export const ValueEditorRender = React.forwardRef<HTMLElement, IDataEditControlRenderer>(
    (
        { editRenderUI, displayRenderUI, forData, value, onUpdate, mode, navigate, editMode }: IDataEditControlRenderer,
        ref,
    ) => {
        const [updated, setUpdated] = React.useState<AppGlueAny>(value);

        useEffect(() => {
            setUpdated(value);
        }, [value]);

        let content: AppGlueAny;
        if (Array.isArray(value) && typeof value !== "string" && value.length) {
            const lengths = value.map((a: AppGlueAny) => (a as string)?.length || 0);
            content = value[lengths.indexOf(Math.max(...lengths))]?.toString() || "";
        } else {
            let valueStr = value !== null && value !== undefined ? `${value}` : "";
            content = valueStr.length > 15 ? `${valueStr.slice(0, 15)}...` : valueStr.toString();
        }

        const onValueUpdate = (newValue: AppGlueAny) => {
            if (Array.isArray(newValue)) {
                setUpdated([...newValue]);
            } else if (typeof newValue === "object") {
                setUpdated({ ...newValue });
            } else setUpdated(newValue);

            onUpdate(newValue);
        };

        return (
            <ValueEditorWrapper
                onClick={() => (navigate ? navigate("Select") : undefined)}
                className="ValueEditor-root"
                content={content}
            >
                {mode === "display_mode" ? (
                    <>
                        {React.createElement(displayRenderUI, {
                            value: updated,
                            forData,
                        })}
                    </>
                ) : null}
                {mode === "edit_mode" && forData.type !== DataTypes.OBJECT ? (
                    <EditModeValueEditorRender
                        content={content}
                        className="XValueEditor-editWrapper"
                    >
                        {React.createElement(editRenderUI, {
                            value: updated,
                            forData,
                            onUpdate: onValueUpdate,
                            editMode: editMode || "inline",
                            ref,
                        })}
                    </EditModeValueEditorRender>
                ) : null}
                {mode === "display_mode_edit" || (forData.type === DataTypes.OBJECT && mode === "edit_mode") ? (
                    <DisplayEditModeValueEditorRender
                        value={updated}
                        forData={forData}
                        onUpdate={onValueUpdate}
                        mode={mode}
                        editMode={editMode || "inline"}
                        displayRenderUI={displayRenderUI}
                        editRenderUI={editRenderUI}
                        ref={ref}
                    />
                ) : null}
            </ValueEditorWrapper>
        );
    },
);

export interface IDisplayEditModeValueEditor {
    forData: IDataDefinition;
    value: AppGlueAny;
    onUpdate: (newValue: AppGlueAny) => void;
    mode: IValueEditorMode;
    editMode: "inline" | "layer";
    editRenderUI: React.ForwardRefExoticComponent<
        React.PropsWithoutRef<IEditModeValueEditor> & React.RefAttributes<HTMLElement>
    >;
    displayRenderUI: (props: IDisplayModeValueEditor) => JSX.Element | null;
}

export const DisplayEditModeValueEditorRender = React.forwardRef<HTMLElement, IDisplayEditModeValueEditor>(
    ({ value, forData, onUpdate, editMode, editRenderUI, displayRenderUI }: IDisplayEditModeValueEditor, ref) => {
        const [mode, setMode] = useState<IValueEditorMode>("display_mode");

        const [updated, setUpdated] = useState<AppGlueAny>(value);

        const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

        const onValueUpdate = (newValue: AppGlueAny) => {
            if (Array.isArray(newValue)) {
                setUpdated([...newValue]);
            } else {
                setUpdated(newValue);
            }

            if (editMode === "inline") {
                onUpdate(newValue);
            }
        };

        useEffect(() => {
            if (Array.isArray(value)) {
                setUpdated([...value]);
            } else if (typeof value === "object") {
                setUpdated({ ...value });
            } else {
                setUpdated(value);
            }
        }, [value, setUpdated]);

        const onClear = (event: React.MouseEvent<HTMLAnchorElement>) => {
            event.stopPropagation();
            onUpdate(undefined);
        };

        const onDone = (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();

            onUpdate(updated);
            setMode("display_mode");
            setAnchorEl(null);
        };

        if (editMode === "inline" && forData.type !== DataTypes.OBJECT) {
            let content: AppGlueAny;
            if (Array.isArray(updated) && typeof updated !== "string") {
                const lengths = value.map((a: AppGlueAny) => (a as string)?.length || 0);
                content = updated[lengths.indexOf(Math.max(...lengths))].toString();
            } else {
                content = (updated || "").toString();
            }

            return (
                <DisplayEditModeRender
                    onClick={() => setMode("edit_mode")}
                    data-testid="ValueEditor-DisplayEdit-Inline"
                >
                    {mode === "display_mode" ? (
                        <>
                            {React.createElement(displayRenderUI, {
                                value,
                                forData,
                            })}
                        </>
                    ) : (
                        <ClickAwayListener onClickAway={() => setMode("display_mode")}>
                            <EditModeValueEditorRender content={content}>
                                {React.createElement(editRenderUI, {
                                    value: updated,
                                    forData,
                                    editMode: "inline",
                                    onUpdate: onValueUpdate,
                                    ref,
                                })}
                            </EditModeValueEditorRender>
                        </ClickAwayListener>
                    )}
                </DisplayEditModeRender>
            );
        }

        return (
            <DisplayEditModeRender
                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    setMode("edit_mode");
                    setAnchorEl(event.currentTarget);
                }}
                data-testid="ValueEditor-DisplayEdit-Layer"
            >
                {displayRenderUI({ value, forData })}

                {forData.type !== DataTypes.OBJECT ? (
                    <EditLayerWrapper
                        open={!!anchorEl}
                        anchorEl={anchorEl}
                        onClose={() => {
                            setAnchorEl(null);
                            setMode("display_mode");
                        }}
                    >
                        {React.createElement(editRenderUI, {
                            value: updated,
                            forData,
                            editMode: "inline",
                            onUpdate: onValueUpdate,
                            ref,
                        })}

                        <ButtonGroupRender>
                            <StyledButton
                                sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                variant="contained"
                                onClick={onDone}
                                data-testid="XValueEditor-done"
                            >
                                Done
                            </StyledButton>
                        </ButtonGroupRender>
                    </EditLayerWrapper>
                ) : (
                    <UILayer
                        open={!!anchorEl}
                        onClose={() => {
                            setAnchorEl(null);
                            setMode("display_mode");
                        }}
                        padding="16px 20px"
                        renderUI={() => (
                            <LayerRender>
                                {React.createElement(editRenderUI, {
                                    value: updated,
                                    forData,
                                    editMode: "inline",
                                    onUpdate: onValueUpdate,
                                    ref,
                                })}

                                <ButtonGroupRender>
                                    <StyledLink onClick={onClear}>Clear</StyledLink>
                                    <StyledButton
                                        sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                        variant="contained"
                                        onClick={onDone}
                                    >
                                        Done
                                    </StyledButton>
                                </ButtonGroupRender>
                            </LayerRender>
                        )}
                    />
                )}
            </DisplayEditModeRender>
        );
    },
);

const ButtonGroupRender = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    padding: 15px 0 10px;
`;

const LayerRender = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .ValueEditor-display {
        flex: 1;
        overflow: auto;
    }
`;
